/**
 * Maps a TYPE_DEF graft model node to ClassData: namespace, name, constructors, methods, fields, and nested children (TypeDefs).
 * TYPE_DEF payload: [NAMESPACE, TYPENAME, MODULE_FULLNAME, MODULE_HASH, MODIFIER, ...members]
 */

import type { ClassData, Constructor, Field, Method, TypeDef } from '@/core/types/library.type'

import { TYPE_DEF } from '../../const/graftModelPayload.const'
import { UnifiedGraftModelTypeKey } from '../../const/unifiedGraftModelTypeKeys.const'
import { getUnifiedGraftModelType } from '../getUnifiedGraftModelType'
import { getUniqueKey } from '../getUniqueKey'
import { isMember } from '../isMember'
import { isTypeDef } from '../isTypeDef'

import { mapConstructor } from './mapConstructor'
import { mapField } from './mapField'
import { mapMethod } from './mapMethod'
import { safeString } from './safeString'

const MEMBER_KIND = {
  FIELD: new Set([UnifiedGraftModelTypeKey.STATIC_FIELD, UnifiedGraftModelTypeKey.INSTANCE_FIELD]),
  CONSTRUCTOR: new Set([UnifiedGraftModelTypeKey.CONSTRUCTOR, UnifiedGraftModelTypeKey.OVERLOADED_CONSTRUCTOR]),
  METHOD: new Set([
    UnifiedGraftModelTypeKey.INSTANCE_METHOD,
    UnifiedGraftModelTypeKey.STATIC_METHOD,
    UnifiedGraftModelTypeKey.OVERLOADED_INSTANCE_METHOD,
    UnifiedGraftModelTypeKey.OVERLOADED_STATIC_METHOD,
  ]),
} as const

export const mapClass = (item: TypeDef): ClassData => {
  const p = item.payload
  const members = (p.slice(TYPE_DEF.MEMBERS_FROM) as unknown[]).filter(isMember) as (
    | Method
    | Constructor
    | Field
    | TypeDef
  )[]

  const getKind = (m: unknown) =>
    getUnifiedGraftModelType(m as { unifiedGraftModelType?: unknown; graftModelType?: unknown })

  const nestedTypeDefs = members.filter(isTypeDef).map(mapClass)
  const fields = members.filter((m) => MEMBER_KIND.FIELD.has(getKind(m))).map((m) => mapField(m as Field))
  const constructors = members
    .filter((m) => MEMBER_KIND.CONSTRUCTOR.has(getKind(m)))
    .map((m) => mapConstructor(m as Constructor))
  const methods = members.filter((m) => MEMBER_KIND.METHOD.has(getKind(m))).map((m) => mapMethod(m as Method))

  return {
    namespace: safeString(p[TYPE_DEF.NAMESPACE]),
    name: safeString(p[TYPE_DEF.TYPENAME]),
    moduleFullName: safeString(p[TYPE_DEF.MODULE_FULLNAME]),
    constructors: Object.fromEntries(constructors.map((c, i) => [getUniqueKey(c, i), c])),
    methods: Object.fromEntries(methods.map((m, i) => [getUniqueKey(m, i), m])),
    fields: fields.length > 0 ? Object.fromEntries(fields.map((f) => [f.name, f])) : undefined,
    children:
      nestedTypeDefs.length > 0 ? Object.fromEntries(nestedTypeDefs.map((c, i) => [`${c.name}-${i}`, c])) : undefined,
  }
}
